// Starter-site presets — one per app type. This module is the single source
// of truth for template content: the SPA renders it as the default when the
// instance's `config/site` row is missing, and scripts/seed-template.mjs
// seeds the same preset INTO that row so operators can edit everything live
// (Dev Console config editor or any signed-in client) without a deploy.
//
// Shape (all fields optional — the chassis skips what's absent):
//   brand      site name in the nav
//   accent     CSS color — the per-type brand tint layered over the kit tokens
//   layout     'editorial' — lookbook chassis (wide wrap, tracked caps,
//              image-led borderless cards); absent = the default chassis
//   hero       { kicker, title, sub, cta: {label, href}, art: 'a'|'b'|'c',
//                image?, alt?, media?, interval? } — `image` = full-bleed
//                photo instead of art; `media` = crossfading loop of slides
//                [{image, alt?, position?} | {video, poster?}] (muted inline
//                video plays through, then the loop advances); `interval` =
//                ms per image slide (4500)
//   nav        [{ label, href }] — anchors into section ids or /p/:id pages
//   sections   ordered [{ kind, id, title, sub?, ...kind-specific }]
//     kinds: features {items:[{icon,title,text}]}
//            tiers {items:[{name,price,per,blurb,bullets[],cta}]}
//            cards {style:'post'|'product'|'case', itemsFrom:'items'}
//            team {items:[{initials,name,role,bio}]}
//            quotes {items:[{quote,name,role}]}
//            stats {items:[{value,label}]}
//            cta {title,sub,button:{label,href}}
//            text {body} — long-form (disclosures, about)
//            gallery {items:[{image,alt,caption,href?,span:'full'|'half'}]}
//                — editorial photo grid (operator-uploaded imagery)
//            faq {itemsFrom:'faq'} — accordion of {q,a}; the same rows seed
//                the public `knowledge` corpus so the site assistant answers them
//   faq        [{ q, a }] — rendered by the 'faq' section + seeded as knowledge
//   items      [{ id, kind, title, tag, meta, price?, excerpt, body, hue,
//                image?, alt? }] — rendered by 'cards' sections and the
//              /p/:id detail page; `image` replaces the gradient cover
//   footer     { text }
// Presets ship no binary assets: covers are CSS gradients keyed by `hue` so
// the SPA cache stays lean and every spawn looks sharp with zero uploads.
// Operators add `image` urls (e.g. static files under /brand/) when they
// have real photography.

export const PRESETS = {
  // ---- Content blog (image / video / text posts) --------------------------------
  blog: {
    brand: 'The Journal',
    accent: '#e0a414',
    hero: {
      kicker: 'A publication built on your own stack',
      title: 'Stories, essays, and field notes',
      sub: 'Long-form writing, photo essays, and video features — published from the same platform that runs your pipelines and agents.',
      cta: { label: 'Start reading', href: '#latest' },
      art: 'a',
    },
    nav: [
      { label: 'Latest', href: '#latest' },
      { label: 'About', href: '#about' },
    ],
    sections: [
      { kind: 'cards', id: 'latest', title: 'Latest posts', style: 'post', itemsFrom: 'items' },
      {
        kind: 'text',
        id: 'about',
        title: 'About this publication',
        body: 'This site ships with your app instance. Every post here is demo content — replace it from the site editor, or wire the posts section to your own collection. Image, video, and text posts all render from the same card grid.',
      },
      { kind: 'cta', title: 'Get every issue', sub: 'One email when something new lands. No noise.', button: { label: 'Subscribe', href: '/login' } },
      { kind: 'faq', id: 'faq', title: 'Questions, answered', itemsFrom: 'faq' },
    ],
    items: [
      { id: 'field-notes-01', kind: 'post', title: 'Field notes: shipping week', tag: 'Essay', meta: 'Issue 12 · 6 min read', hue: 32, excerpt: 'What actually happens the week a product ships — the checklists, the nerves, and the one bug that always waits until Friday.', body: 'What actually happens the week a product ships — the checklists, the nerves, and the one bug that always waits until Friday.\n\nThis is placeholder long-form content. Edit it in the site editor: every post lives in the site document as plain text, and the detail page renders paragraphs as written.\n\nReplace these posts with your own — or point the cards section at a real collection when you outgrow the starter.' },
      { id: 'photo-essay-harbor', kind: 'post', title: 'The harbor at 5am', tag: 'Photo essay', meta: 'Issue 11 · 24 photographs', hue: 200, excerpt: 'Before the first ferry: cranes, gulls, and the slow choreography of a working waterfront.', body: 'Before the first ferry: cranes, gulls, and the slow choreography of a working waterfront.\n\nA photo essay placeholder — swap the gradient covers for uploaded stills whenever you are ready; the layout already handles tall media.' },
      { id: 'video-interview-maker', kind: 'post', title: 'Interview: the maker of things', tag: 'Video', meta: 'Issue 11 · 18 min watch', hue: 280, excerpt: 'A conversation about craft, tools, and why the boring parts of the work are the parts that matter.', body: 'A conversation about craft, tools, and why the boring parts of the work are the parts that matter.\n\nVideo posts embed wherever your media lives — this starter keeps a text summary so the page reads well before you wire the player.' },
      { id: 'essay-slow-software', kind: 'post', title: 'In defense of slow software', tag: 'Essay', meta: 'Issue 10 · 9 min read', hue: 150, excerpt: 'Fast to build, slow to break: an argument for software that ages like a cast-iron pan.', body: 'Fast to build, slow to break: an argument for software that ages like a cast-iron pan.\n\nPlaceholder body copy — the detail page renders every paragraph of this text.' },
    ],
    faq: [
      { q: 'How often do you publish?', a: 'New posts land weekly — essays on Tuesdays, interviews and video when they are ready. Subscribe from the footer to get each issue by email.' },
      { q: 'Can I republish or quote a post?', a: 'Quote freely with a link back. For full republication, write to us first — we usually say yes to non-commercial use.' },
      { q: 'Do you accept guest posts?', a: 'Occasionally. Pitch a one-paragraph summary and two links to your previous writing through the contact form.' },
      { q: 'How do I sign in?', a: 'Members sign in with the “Open app” link in the top right. Sign-in is by invitation; ask the editor for access.' },
      { q: 'Where do the images and video come from?', a: 'Everything on the site is produced by the editorial team unless the post credits someone else.' },
    ],
    footer: { text: 'The Journal — published on the nventr app kit.' },
  },

  // ---- SaaS company site ----------------------------------------------------------
  saas: {
    brand: 'Acme Cloud',
    accent: '#6366f1',
    hero: {
      kicker: 'Now in general availability',
      title: 'The fastest way to run your operations',
      sub: 'One platform for the workflows your team actually runs — automated pipelines, human review where it matters, and an audit trail for all of it.',
      cta: { label: 'Start free', href: '/login' },
      art: 'b',
    },
    nav: [
      { label: 'Product', href: '#product' },
      { label: 'Pricing', href: '#pricing' },
      { label: 'Customers', href: '#customers' },
    ],
    sections: [
      {
        kind: 'features',
        id: 'product',
        title: 'Everything in one place',
        items: [
          { icon: '⚡', title: 'Automated pipelines', text: 'Build gated workflows visually and run them on your own compute — every side effect reviewed or filtered.' },
          { icon: '🧭', title: 'Human-in-the-loop', text: 'Approvals land in a review queue with full context. Nothing ships without a sign-off you configured.' },
          { icon: '🧾', title: 'Audit everything', text: 'Every tool call, approval, and denial lands in an append-only audit trail your compliance team will love.' },
          { icon: '🔌', title: 'Bring your own models', text: 'OpenAI, xAI, Azure, Gemini, or Bedrock — swap providers in config, not in code.' },
        ],
      },
      { kind: 'stats', items: [{ value: '99.9%', label: 'uptime SLO' }, { value: '<200ms', label: 'median API response' }, { value: 'SOC2', label: 'audit-ready trail' }] },
      {
        kind: 'tiers',
        id: 'pricing',
        title: 'Simple pricing',
        items: [
          { name: 'Starter', price: '$49', per: '/mo', blurb: 'For small teams getting going.', bullets: ['5 seats', 'Unlimited pipelines', 'Community support'], cta: { label: 'Start free', href: '/login' } },
          { name: 'Growth', price: '$199', per: '/mo', blurb: 'For teams running real volume.', bullets: ['25 seats', 'HITL review queues', 'Priority support'], cta: { label: 'Start free', href: '/login' } },
          { name: 'Enterprise', price: 'Custom', per: '', blurb: 'Your cloud, your controls.', bullets: ['Unlimited seats', 'Self-hosted option', 'Dedicated support'], cta: { label: 'Talk to us', href: '#contact' } },
        ],
      },
      {
        kind: 'quotes',
        id: 'customers',
        title: 'Teams run on it',
        items: [
          { quote: 'We replaced four internal tools the first month. The audit trail alone paid for it.', name: 'Dana R.', role: 'VP Operations' },
          { quote: 'The review queue is the feature nobody markets and everybody needs.', name: 'Sam K.', role: 'Head of Compliance' },
        ],
      },
      { kind: 'cta', id: 'contact', title: 'Ready when you are', sub: 'Spin up a workspace in minutes — no card required.', button: { label: 'Create your account', href: '/login' } },
      { kind: 'faq', id: 'faq', title: 'Questions, answered', itemsFrom: 'faq' },
    ],
    items: [],
    faq: [
      { q: 'Is there a free plan?', a: 'Yes — the Starter tier is free to try with no card required. Upgrade from inside the workspace when you need more seats or priority support.' },
      { q: 'How does billing work?', a: 'Plans bill monthly per workspace. You can change plans or cancel at any time; changes apply on the next cycle.' },
      { q: 'Where is my data stored?', a: 'In the region you choose at signup. Enterprise plans can run fully self-hosted on your own infrastructure.' },
      { q: 'Do you offer single sign-on?', a: 'SAML and OIDC single sign-on are included on the Enterprise tier, with SCIM provisioning on request.' },
      { q: 'How do I get support?', a: 'Community support on Starter, priority email on Growth, and a dedicated contact with response SLAs on Enterprise.' },
      { q: 'Can I export my data?', a: 'Yes. Every workspace can export its records and audit trail from Settings at any time.' },
    ],
    footer: { text: 'Acme Cloud — built on the nventr app kit.' },
  },

  // ---- Retail / product ecommerce ---------------------------------------------------
  commerce: {
    brand: 'Meridian Goods',
    accent: '#f97316',
    hero: {
      kicker: 'Small-batch, shipped worldwide',
      title: 'Goods made to be used daily',
      sub: 'A working storefront starter: catalog, product pages, and an order-inquiry flow — swap in your products and go.',
      cta: { label: 'Shop the collection', href: '#shop' },
      art: 'c',
    },
    nav: [
      { label: 'Shop', href: '#shop' },
      { label: 'Our story', href: '#story' },
    ],
    sections: [
      { kind: 'cards', id: 'shop', title: 'The collection', style: 'product', itemsFrom: 'items' },
      {
        kind: 'text',
        id: 'story',
        title: 'Our story',
        body: 'Placeholder brand story — tell customers who makes the goods and why. This starter renders products from the site document; the product detail pages include an inquiry CTA you can point at your checkout, form, or agent.',
      },
      { kind: 'quotes', title: 'From our customers', items: [{ quote: 'The workbag has survived two winters and looks better for it.', name: 'Ari M.', role: 'verified buyer' }, { quote: 'Shipped fast, packaged like a gift.', name: 'Jo T.', role: 'verified buyer' }] },
      { kind: 'cta', title: 'Wholesale & custom runs', sub: 'We take a small number of custom orders each quarter.', button: { label: 'Get in touch', href: '/login' } },
      { kind: 'faq', id: 'faq', title: 'Questions, answered', itemsFrom: 'faq' },
    ],
    items: [
      { id: 'workbag-canvas', kind: 'product', title: 'Canvas workbag', tag: 'Bags', price: '$148', meta: 'Waxed canvas · brass hardware', hue: 24, excerpt: 'A 20L daily carry in 18oz waxed canvas with a laptop sleeve and a lifetime repair promise.', body: 'A 20L daily carry in 18oz waxed canvas with a laptop sleeve and a lifetime repair promise.\n\nPlaceholder product copy — dimensions, materials, care. Edit in the site editor.' },
      { id: 'field-mug', kind: 'product', title: 'Enamel field mug', tag: 'Kitchen', price: '$28', meta: 'Steel core · 12oz', hue: 200, excerpt: 'Campfire-proof, dishwasher-safe, and stackable — the mug that outlives the trip.', body: 'Campfire-proof, dishwasher-safe, and stackable — the mug that outlives the trip.\n\nPlaceholder product copy.' },
      { id: 'bench-knife', kind: 'product', title: 'Bench knife no. 4', tag: 'Tools', price: '$96', meta: 'O1 tool steel · walnut', hue: 130, excerpt: 'A fixed-blade shop knife ground for control, with a walnut handle that wears in, not out.', body: 'A fixed-blade shop knife ground for control, with a walnut handle that wears in, not out.\n\nPlaceholder product copy.' },
      { id: 'wool-throw', kind: 'product', title: 'Lambswool throw', tag: 'Home', price: '$180', meta: '100% lambswool · 130×180cm', hue: 320, excerpt: 'Woven in small batches; heavy enough to matter, light enough for summer evenings.', body: 'Woven in small batches; heavy enough to matter, light enough for summer evenings.\n\nPlaceholder product copy.' },
    ],
    faq: [
      { q: 'How long does shipping take?', a: 'Orders ship within two business days and arrive in three to five days in the continental US. International delivery takes seven to fourteen days.' },
      { q: 'What is your return policy?', a: 'Returns are accepted within 30 days of delivery for unused items in original packaging. Start a return from your order confirmation email.' },
      { q: 'Do you ship internationally?', a: 'Yes, to most countries. Duties and taxes are calculated at checkout where available; otherwise they are collected on delivery.' },
      { q: 'How do I track my order?', a: 'A tracking link is emailed as soon as your order ships. Signed-in customers also see it under their orders.' },
      { q: 'Are the products guaranteed?', a: 'Every product carries a one-year warranty against defects in materials and workmanship.' },
      { q: 'Can I change or cancel an order?', a: 'Within two hours of placing it, yes — reply to your confirmation email. After that the order is already being packed.' },
    ],
    footer: { text: 'Meridian Goods — storefront starter on the nventr app kit.' },
  },

  // ---- Institution (funds, VCs, banks) ---------------------------------------------
  institution: {
    brand: 'Northbridge Capital',
    accent: '#0ea5e9',
    hero: {
      kicker: 'Est. 2011 · $2.4B under management',
      title: 'Patient capital for durable businesses',
      sub: 'We back operators building for decades, not quarters — with committed capital, quiet counsel, and no tourist money.',
      cta: { label: 'Our approach', href: '#approach' },
      art: 'b',
    },
    nav: [
      { label: 'Approach', href: '#approach' },
      { label: 'Team', href: '#team' },
      { label: 'Contact', href: '#contact' },
    ],
    sections: [
      {
        kind: 'features',
        id: 'approach',
        title: 'How we invest',
        items: [
          { icon: '◆', title: 'Concentrated positions', text: 'A dozen partnerships at a time. Enough to matter to us; few enough that we matter to you.' },
          { icon: '◆', title: 'Committed horizons', text: 'Ten-year vehicles with evergreen follow-on. We underwrite cycles, not exits.' },
          { icon: '◆', title: 'Operating help, on request', text: 'Former operators on staff — hiring, pricing, and the unglamorous middle of company building.' },
        ],
      },
      { kind: 'stats', items: [{ value: '$2.4B', label: 'assets under management' }, { value: '38', label: 'portfolio partnerships' }, { value: '13yr', label: 'median holding period' }] },
      {
        kind: 'team',
        id: 'team',
        title: 'The partnership',
        items: [
          { initials: 'EN', name: 'E. Northbridge', role: 'Managing Partner', bio: 'Two decades allocating through three cycles; previously an operator in payments.' },
          { initials: 'MK', name: 'M. Kessler', role: 'Partner, Research', bio: 'Leads underwriting. Writes the memos everyone else quotes.' },
          { initials: 'RV', name: 'R. Vance', role: 'Partner, Operations', bio: 'Runs the operating bench and the LP program.' },
        ],
      },
      { kind: 'cta', id: 'contact', title: 'For founders and LPs', sub: 'We read every note. Introductions help, but conviction travels on its own.', button: { label: 'Write to us', href: '/login' } },
      {
        kind: 'text',
        title: 'Disclosures',
        body: 'Placeholder disclosures — this starter content is not investment advice, an offer, or a solicitation. Replace with your regulatory disclosures, ADV links, and legal entity details before going live.',
      },
      { kind: 'faq', id: 'faq', title: 'Questions, answered', itemsFrom: 'faq' },
    ],
    items: [],
    faq: [
      { q: 'Who can invest with the firm?', a: 'We work with qualified institutional investors and accredited individuals. Minimum commitments are set per fund.' },
      { q: 'How do I request the latest letter or memo?', a: 'Current investors receive letters by email each quarter. Prospective investors can request a sample memo through the contact form.' },
      { q: 'Where are you based?', a: 'Our offices are in the city listed in the footer; the team works across the regions we invest in.' },
      { q: 'How are fees structured?', a: 'Fees are described in each fund’s offering documents and discussed directly with prospective investors before any commitment.' },
      { q: 'How do I reach investor relations?', a: 'Use the contact form or the investor relations address in the footer. We respond within two business days.' },
    ],
    footer: { text: 'Northbridge Capital — institutional starter on the nventr app kit.' },
  },

  // ---- Agency / portfolio ------------------------------------------------------------
  agency: {
    brand: 'Studio Forma',
    accent: '#ec4899',
    hero: {
      kicker: 'Design & engineering studio',
      title: 'We build the thing you actually meant',
      sub: 'Brand, product, and software — one senior team from first sketch to shipped. Selected work below.',
      cta: { label: 'See the work', href: '#work' },
      art: 'a',
    },
    nav: [
      { label: 'Work', href: '#work' },
      { label: 'Services', href: '#services' },
      { label: 'Contact', href: '#contact' },
    ],
    sections: [
      { kind: 'cards', id: 'work', title: 'Selected work', style: 'case', itemsFrom: 'items' },
      {
        kind: 'features',
        id: 'services',
        title: 'What we do',
        items: [
          { icon: '✦', title: 'Brand & identity', text: 'Naming, systems, and the voice your product speaks in.' },
          { icon: '✦', title: 'Product design', text: 'Research to prototype to spec — shipped with the engineers in the room.' },
          { icon: '✦', title: 'Full-stack build', text: 'Web platforms and internal tools, delivered on infrastructure you own.' },
        ],
      },
      { kind: 'cta', id: 'contact', title: 'Have a project?', sub: 'Two new engagements per quarter. Tell us what you are making.', button: { label: 'Start a conversation', href: '/login' } },
      { kind: 'faq', id: 'faq', title: 'Questions, answered', itemsFrom: 'faq' },
    ],
    items: [
      { id: 'case-harbor-bank', kind: 'case', title: 'Harbor Bank — digital relaunch', tag: 'Fintech', meta: 'Brand · Product · Build', hue: 210, excerpt: 'A 90-year-old bank, re-platformed: new identity, new onboarding, 4× conversion on account opening.', body: 'A 90-year-old bank, re-platformed: new identity, new onboarding, 4× conversion on account opening.\n\nPlaceholder case study — challenge, approach, outcome. Edit in the site editor.' },
      { id: 'case-fielday', kind: 'case', title: 'Fielday — zero to launch', tag: 'Consumer', meta: 'Product · Build', hue: 140, excerpt: 'From napkin to 50k users: a field-logging app for agronomists, shipped in fourteen weeks.', body: 'From napkin to 50k users: a field-logging app for agronomists, shipped in fourteen weeks.\n\nPlaceholder case study.' },
      { id: 'case-atlas-internal', kind: 'case', title: 'Atlas — internal tools', tag: 'B2B', meta: 'Design systems · Build', hue: 40, excerpt: 'One design system, eleven internal apps, and a support team that stopped filing UI tickets.', body: 'One design system, eleven internal apps, and a support team that stopped filing UI tickets.\n\nPlaceholder case study.' },
    ],
    faq: [
      { q: 'What does an engagement look like?', a: 'Most projects start with a two-week discovery sprint, then move into design and build phases with weekly reviews.' },
      { q: 'How do you price work?', a: 'Fixed-scope projects are quoted up front; ongoing work runs on a monthly retainer. Both are described in the proposal.' },
      { q: 'How long does a typical project take?', a: 'A brand or site project typically runs eight to twelve weeks from kickoff to launch.' },
      { q: 'Do you work with clients outside your city?', a: 'Yes — most of our clients are remote. We meet in person for kickoffs when it helps.' },
      { q: 'Who owns the work?', a: 'You do. Final deliverables, source files and the code are handed over on completion.' },
    ],
    footer: { text: 'Studio Forma — portfolio starter on the nventr app kit.' },
  },

  // ---- Local services ---------------------------------------------------------------
  services: {
    brand: 'Beacon & Co.',
    accent: '#22c55e',
    hero: {
      kicker: 'Serving the metro area since 2009',
      title: 'Reliable service, on your schedule',
      sub: 'Licensed, insured, and on time — book a visit online and get a confirmation within the hour.',
      cta: { label: 'Book a visit', href: '#contact' },
      art: 'c',
    },
    nav: [
      { label: 'Services', href: '#offerings' },
      { label: 'Reviews', href: '#reviews' },
      { label: 'Book', href: '#contact' },
    ],
    sections: [
      {
        kind: 'features',
        id: 'offerings',
        title: 'What we offer',
        items: [
          { icon: '🛠', title: 'Maintenance plans', text: 'Seasonal check-ups on a schedule — we call you, not the other way around.' },
          { icon: '🚨', title: 'Emergency call-outs', text: 'Same-day response, seven days a week, with upfront pricing before work starts.' },
          { icon: '📋', title: 'Inspections & reports', text: 'Written condition reports with photos — for closings, insurance, or peace of mind.' },
        ],
      },
      { kind: 'stats', items: [{ value: '4.9★', label: 'average review' }, { value: '<1hr', label: 'booking confirmation' }, { value: '15yr', label: 'serving the area' }] },
      {
        kind: 'quotes',
        id: 'reviews',
        title: 'What neighbors say',
        items: [
          { quote: 'Showed up on time, fixed it the first visit, and left the place cleaner than they found it.', name: 'P. Alvarez', role: 'Maple St.' },
          { quote: 'The maintenance plan already caught two problems before they got expensive.', name: 'D. Chen', role: 'Riverside' },
        ],
      },
      { kind: 'cta', id: 'contact', title: 'Book your visit', sub: 'Tell us what you need and pick a window — confirmation within the hour.', button: { label: 'Request a booking', href: '/login' } },
      { kind: 'faq', id: 'faq', title: 'Questions, answered', itemsFrom: 'faq' },
    ],
    items: [],
    faq: [
      { q: 'What areas do you serve?', a: 'We serve the metro area and surrounding towns listed on the service-area page. Call if you are just outside it.' },
      { q: 'How soon can someone come out?', a: 'Standard appointments are usually available within two business days; emergency calls are dispatched the same day.' },
      { q: 'Are you licensed and insured?', a: 'Yes. Our license number is on every estimate and invoice, and we carry full liability and workers’ compensation coverage.' },
      { q: 'How do estimates work?', a: 'Estimates are free for most jobs. We quote a fixed price before work starts and only change it with your approval.' },
      { q: 'Do you offer a maintenance plan?', a: 'Yes — the annual plan includes two inspections, priority scheduling and a discount on repairs.' },
      { q: 'What payment methods do you accept?', a: 'Card, bank transfer and check. Payment is due on completion unless the estimate says otherwise.' },
    ],
    footer: { text: 'Beacon & Co. — services starter on the nventr app kit.' },
  },
};

export const TEMPLATE_TYPES = Object.keys(PRESETS);
export const getPreset = (type) => PRESETS[type] || null;
